import React, { useCallback, useEffect, useState } from "react";

import { BadgeCheck, ShieldAlert, ShieldQuestion, Loader2, ShieldPlus } from "lucide-react";

import { ComplianceService } from "@/services/complianceService";
import useModuleAccess from "@/hooks/useModuleAccess";
import { MODULES } from "@/permissions/modules";
import useLiveResource from "@/hooks/useLiveResource";
import { useAuth } from "@/hooks/useAuth";
import { PERMISSIONS } from "@/permissions/constant";
import { getUserPermissions, hasPermission } from "@/permissions/rbac";
import { Button } from "@/components/ui/Button";
import SendToComplianceModal from "@/pages/admin/candidate/components/SendToComplianceModal";
import ComplianceEmailModal from "@/pages/admin/candidate/components/ComplianceEmailModal";
import ChecklistBuilderModal from "@/pages/admin/compliance/checklists/components/ChecklistBuilderModal";

/**
 * Compliance state for the candidate on the profile screen (Phase 7B).
 *
 * Four states, and each answers a different question the recruiter is actually
 * asking when they look at a gated application:
 *
 *   Yet to Send for Compliance  reached the trigger status, nobody has sent them
 *   In Compliance process       sent, below the approval threshold
 *   n% compliant                at/above the threshold — green, with a tick
 *   Compliance Rejected         rejected, with the reasons on hover
 *
 * Connectivity Phase B splits the first of those in two. "Yet to Send" names an
 * action and offered no way to take it — the recruiter had to find a different
 * screen to do the thing this pill was asking them to do. A recruiter holding
 * COMPLIANCE_CREATE now gets a button in the same box; a watcher without it
 * still gets the pill, which tells them why they cannot act. Both are built
 * from `WRAP`, so the header row keeps its height when the branch changes.
 *
 * Renders NOTHING when compliance is not entitled for the tenant, or when the
 * candidate never reached the trigger status. An indicator that says
 * "Yet to Send" for someone who has only just applied is noise, and worse,
 * implies an action the recruiter is not yet supposed to take.
 *
 * Live via the Phase 2 websocket events — approvals accrue on the compliance
 * side, and a recruiter watching this screen should see the percentage move
 * without reloading. No new event was added for this; it reuses the ones the
 * approve/reject/upload paths already emit.
 */

const WRAP = "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-medium border";

// The send button is the one control in this row — every other branch is a
// status pill, which is read and not clicked. It carries a little more height
// and padding than `WRAP` so it reads as pressable at a glance, and keeps
// `WRAP`'s pill shape and font size so the header row neither changes height
// nor gains a second visual language when the branch changes.
const SEND_BUTTON = "inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[12px] font-semibold";

const ComplianceIndicator = ({ candidateId, candidateName, applicationId, applicationStatusId, jobId, jobTitle, className = "" }) => {
  const { isAvailable, canUse } = useModuleAccess(MODULES.COMPLIANCE);
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  // The checklist the send modal asked to show, read-only. Held HERE rather
  // than inside that modal so the builder is a SIBLING of it: a dialog mounted
  // inside another's content creates a stacking context it can never paint
  // above (React error #299). Same shape job-detail's compliance card uses.
  const [viewChecklistId, setViewChecklistId] = useState(null);
  // Step 2 of the send — a third sibling, never nested (React error #299).
  // Holds the checklist step 1 chose; null means it is closed.
  const [emailStep, setEmailStep] = useState(null);

  // `canUse`, not `isAvailable`: sending is an action, and `useModuleAccess`
  // reserves `canUse` for those — a module that is temporarily down must not
  // offer a control whose route will refuse it.
  const canSend = hasPermission(PERMISSIONS.COMPLIANCE_CREATE, getUserPermissions(user)) && canUse;

  const load = useCallback(async () => {
    if (!candidateId || !isAvailable) return;
    setLoading(true);
    try {
      // `applicationId` is what lets the answer distinguish "this job's run"
      // from "another job's run" (Phase C). Without it the server cannot say
      // which is which, and the indicator behaves as it did before.
      const res = await ComplianceService.getCandidateComplianceState(candidateId, applicationId);
      setState(res ?? null);
    } catch {
      // Non-fatal by design: this is an informational badge on someone
      // else's screen. A compliance outage must not break the candidate
      // profile, so a failure renders nothing rather than an error.
      setState(null);
    } finally {
      setLoading(false);
    }
  }, [candidateId, applicationId, isAvailable]);

  useEffect(() => {
    load();
  }, [load]);

  // The application moving is itself a reason to re-check: crossing into the
  // trigger status is what makes this indicator appear at all.
  useEffect(() => {
    if (applicationStatusId) load();
  }, [applicationStatusId, load]);

  // Only this candidate's events. The audience already limits delivery to
  // people who can see the candidate, but a recruiter sees many — so narrow
  // by id here too, or every officer's action anywhere refetches this badge.
  //
  // The envelope is keyed on the *compliance* candidate id, which is not the
  // recruitment `candidateId` this component is given; `complianceCandidateId`
  // off the loaded state is the bridge between them. Before the first load,
  // and before the candidate has been sent, there is no compliance id to match
  // — and `created` is exactly the event that produces one, so it always
  // passes and lets the first load happen.
  const complianceCandidateId = state?.complianceCandidateId;
  const match = useCallback(
    (envelope) => {
      if (envelope?.event === "compliance_candidate.created") return true;
      if (!complianceCandidateId) return false;
      const own = envelope?.parent?.compliance_candidate ?? envelope?.id;
      return own === complianceCandidateId;
    },
    [complianceCandidateId],
  );

  useLiveResource({
    keys: ["compliance_candidate", "compliance_document"],
    match,
    onChange: load,
  });

  if (!isAvailable) return null;
  if (loading && !state) {
    return (
      <span className={`${WRAP} border-border-primary text-text-primary/50 ${className}`}>
        <Loader2 size={13} className="animate-spin" /> Compliance…
      </span>
    );
  }
  if (!state || !state.applicable) return null;

  if (state.status === "rejected") {
    return (
      <span
        className={`${WRAP} border-red-500/30 bg-red-500/10 text-red-600 ${className}`}
        title={state.rejectionReasons?.length ? state.rejectionReasons.map((r) => `${r.documentName}: ${r.reason}`).join("\n") : "Rejected by compliance"}
      >
        <ShieldAlert size={13} /> Compliance Rejected
      </span>
    );
  }

  // Another application owns the live run (Phase C, Q-Button's middle row).
  // Checked BEFORE the permission fork: this is not about what the recruiter
  // may do, it is about what the route will accept from anyone. Compliance is
  // candidate-grained, so the documents are collected once and a second run
  // cannot start until the first is released.
  if (state.ownedByOtherApplication) {
    const owner = state.owningJobTitle;
    return (
      <span
        className={`${WRAP} cursor-not-allowed border-border-primary bg-background-icon/40 text-text-primary/60 ${className}`}
        title={
          owner
            ? `This candidate is already in compliance for "${owner}". Their documents are collected once, so a second run cannot start until that one is released.`
            : "This candidate is already in compliance for another job. Their documents are collected once, so a second run cannot start until that one is released."
        }
      >
        <ShieldQuestion size={13} aria-hidden="true" /> In compliance for {owner || "another job"}
      </span>
    );
  }

  // The send route refuses a rejected application (applicationRejected is
  // the server's answer for the one being viewed), so no button is offered.
  if (!state.sent && state.applicationRejected) {
    return (
      <span
        className={`${WRAP} cursor-not-allowed border-border-primary bg-background-icon/40 text-text-primary/60 ${className}`}
        title="Rejected candidates cannot be sent to compliance."
      >
        <ShieldQuestion size={13} aria-hidden="true" /> Rejected, cannot send to compliance
      </span>
    );
  }

  if (!state.sent) {
    // Branching on the positive is what makes this fail closed: an
    // unresolved user gives `getUserPermissions` `[]`, `canSend` is false,
    // and the pill renders. A label nobody can act on is noise; a button
    // that 403s is a broken promise.
    if (canSend) {
      return (
        <>
          <Button
            onClick={() => setSendOpen(true)}
            title="Send this candidate to compliance."
            className={`${SEND_BUTTON} border-transparent bg-button-secondary text-text-base transition-colors hover:bg-button-secondary/80 ${className}`}
          >
            <ShieldPlus size={14} aria-hidden="true" /> Send to Compliance
          </Button>
          {/* Both dialogs are siblings of the button and of each other —
            neither nests inside the other's DialogContent (React error #299).
            The send needs no onSent: `compliance_candidate.created` always
            passes `match` above, so the websocket reloads the state and
            this branch gives way to "In Compliance process" on its own. */}
          <SendToComplianceModal
            open={sendOpen && !viewChecklistId && !emailStep}
            onClose={() => setSendOpen(false)}
            candidate={{ id: candidateId, full_name: candidateName }}
            applicationId={applicationId}
            jobId={jobId}
            jobTitle={jobTitle}
            onViewChecklist={setViewChecklistId}
            onNext={({ checklistId }) => setEmailStep({ checklistId })}
          />
          {/* Step 2. `sendOpen` is never cleared while this is up, so Back
            brings the checklist step straight back with its resolved default. */}
          <ComplianceEmailModal
            open={!!emailStep && !viewChecklistId}
            candidate={{ id: candidateId, full_name: candidateName }}
            applicationId={applicationId}
            checklistId={emailStep?.checklistId}
            onBack={() => setEmailStep(null)}
            onClose={() => {
              setEmailStep(null);
              setSendOpen(false);
            }}
          />
          {/* Close-then-reopen, the repo's one answer to two dialogs: the send
            modal is withheld while this is up and returns when it closes,
            because `sendOpen` was never cleared. The resolved checklist is
            re-read on reopen, so Send is still one click away after looking. */}
          {viewChecklistId && <ChecklistBuilderModal open readOnly checklistId={viewChecklistId} onClose={() => setViewChecklistId(null)} />}
        </>
      );
    }

    return (
      <span
        className={`${WRAP} border-amber-500/30 bg-amber-500/10 text-amber-700 ${className}`}
        title="This candidate has reached the stage where compliance starts, but has not been sent yet."
      >
        <ShieldQuestion size={13} /> Yet to Send for Compliance
      </span>
    );
  }

  if (state.meetsThreshold) {
    return (
      <span
        className={`${WRAP} border-emerald-500/30 bg-emerald-500/10 text-emerald-700 ${className}`}
        title="Compliance approved — this application is no longer gated."
      >
        <BadgeCheck size={13} /> The candidate is {state.percent}% compliant
      </span>
    );
  }

  return (
    <span className={`${WRAP} border-blue-500/30 bg-blue-500/10 text-blue-700 ${className}`} title={`${state.percent}% of the required document weight is approved.`}>
      <ShieldQuestion size={13} /> In Compliance process ({state.percent}%)
    </span>
  );
};

export default ComplianceIndicator;
